import { useMemo, useState } from "react";
import { Check, Plus, X } from "lucide-react";
import { btn } from "../Button/buttonStyles";

// Checkbox list (or toggle chips when bare) of suggested values plus a field for
// anything not on the list.
// Shared by Services and Units, in the setup wizard and the System Values module.
const SystemValuePicker = ({
  catalog,
  existingNames = [],
  onAdd,
  onCancel,
  label = "value",
  labelPlural = "values",
  addOtherLabel,
  submitting = false,
  bare = false,
}) => {
  const [selected, setSelected] = useState([]);
  const [custom, setCustom] = useState([]);
  const [customInput, setCustomInput] = useState("");
  const [error, setError] = useState("");

  // Already-saved values stay visible but locked, so it is obvious why they
  // cannot be picked again rather than them silently vanishing from the list.
  const taken = useMemo(
    () => new Set(existingNames.map((n) => n.trim().toLowerCase())),
    [existingNames]
  );

  const isTaken = (name) => taken.has(name.trim().toLowerCase());

  const toggle = (name) => {
    setError("");
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const addCustom = () => {
    const name = customInput.trim();
    if (!name) return;
    if (isTaken(name) || custom.some((c) => c.toLowerCase() === name.toLowerCase())) {
      setError(`"${name}" is already in the list.`);
      return;
    }
    // Typing a catalog value just ticks it rather than adding a duplicate.
    const match = catalog.find((c) => c.toLowerCase() === name.toLowerCase());
    if (match) {
      setSelected((prev) => (prev.includes(match) ? prev : [...prev, match]));
    } else {
      setCustom((prev) => [...prev, name]);
    }
    setCustomInput("");
    setError("");
  };

  // The picker can outlive a save (the wizard keeps it open), so anything that
  // has since been added drops out of the pending selection.
  const pendingCustom = custom.filter((n) => !isTaken(n));
  const chosen = [...selected.filter((n) => !isTaken(n)), ...pendingCustom];
  const submitLabel = submitting
    ? "Adding..."
    : `Add${chosen.length ? ` ${chosen.length}` : ""} ${
        chosen.length === 1 ? label : labelPlural
      }`;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!chosen.length) {
      setError(`Select at least one ${label}.`);
      return;
    }
    onAdd(chosen);
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className={
        bare ? "space-y-4" : "bg-white shadow-sm border border-gray-100 rounded-lg p-5 space-y-5"
      }
    >
      {/* Bare means the host card already explains the step, so skip the intro. */}
      {!bare && (
        <div>
          <p className="text-sm font-medium text-gray-700">
            Select all {labelPlural} you provide.
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Tick as many as you need and add them in one go.
          </p>
        </div>
      )}

      {/* Bare hosts are short on height, so the catalog wraps as toggle chips. */}
      {bare ? (
        <div className="flex flex-wrap gap-2">
          {catalog.map((name) => {
            const added = isTaken(name);
            const on = selected.includes(name);
            const Icon = added || on ? Check : Plus;
            return (
              <button
                key={name}
                type="button"
                onClick={() => toggle(name)}
                disabled={added}
                aria-pressed={on}
                title={added ? "Already added" : undefined}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors ${
                  added
                    ? "border-success-200 bg-success-50 text-success-800 cursor-default"
                    : on
                    ? "border-brand-300 bg-brand-50 text-brand-700"
                    : "border-gray-200 bg-white text-gray-600 hover:border-brand-200 hover:text-brand-700"
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${added || on ? "" : "text-gray-400"}`} />
                {name}
              </button>
            );
          })}
          {pendingCustom.map((name) => (
            <span
              key={name}
              className="inline-flex items-center gap-1.5 rounded-full border border-brand-300 bg-brand-50 pl-3 pr-2 py-1.5 text-[13px] font-medium text-brand-700"
            >
              <Check className="w-3.5 h-3.5" />
              {name}
              <button
                type="button"
                onClick={() => setCustom((prev) => prev.filter((n) => n !== name))}
                className="text-brand-400 hover:text-brand-700"
                aria-label={`Remove ${name}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        {catalog.map((name) => {
          const disabled = isTaken(name);
          return (
            <label
              key={name}
              className={`inline-flex items-start gap-2 text-sm ${
                disabled ? "text-gray-400 cursor-not-allowed" : "text-gray-700 cursor-pointer"
              }`}
            >
              <input
                type="checkbox"
                checked={selected.includes(name)}
                onChange={() => toggle(name)}
                disabled={disabled}
                className="mt-0.5 h-4 w-4 accent-brand-600 rounded cursor-pointer disabled:cursor-not-allowed"
              />
              <span>
                {name}
                {disabled && <span className="text-xs text-gray-400"> (added)</span>}
              </span>
            </label>
          );
        })}
      </div>
      )}

      <div className="pt-3 border-t border-gray-100">
        <label className="mb-1.5 block text-xs font-medium text-gray-600">
          {addOtherLabel || `Add Other ${label}`}
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={customInput}
            onChange={(e) => {
              setCustomInput(e.target.value);
              setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addCustom();
              }
            }}
            placeholder={`Type your own ${label}`}
            className="block w-full text-sm rounded-md border border-gray-200 bg-white px-3 py-2 outline-none transition-colors placeholder:text-gray-400 hover:border-gray-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
          <button
            type="button"
            onClick={addCustom}
            className="shrink-0 inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-brand-600 border border-brand-200 rounded-md hover:bg-brand-50"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
          {bare && (
            <button
              type="submit"
              disabled={submitting || !chosen.length}
              className={btn("primary", "md", "shrink-0 ml-2")}
            >
              {submitLabel}
            </button>
          )}
        </div>

        {!bare && pendingCustom.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {pendingCustom.map((name) => (
              <span
                key={name}
                className="inline-flex items-center gap-1.5 bg-brand-50 text-brand-800 text-xs font-medium pl-2.5 pr-1.5 py-1 rounded-full"
              >
                {name}
                <button
                  type="button"
                  onClick={() => setCustom((prev) => prev.filter((n) => n !== name))}
                  className="text-brand-500 hover:text-brand-700"
                  aria-label={`Remove ${name}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-danger-50 border border-danger-200 text-danger-700 text-sm px-3 py-2 rounded">
          {error}
        </div>
      )}

      {!bare && (
      <div className="pt-3 border-t border-gray-100 flex gap-2 text-sm">
        <button
          type="submit"
          disabled={submitting}
          className={btn("primary", "sm")}
        >
          {submitLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className={btn("secondary", "sm")}
          >
            Cancel
          </button>
        )}
      </div>
      )}
    </form>
  );
};

export default SystemValuePicker;
